import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { FriendRequest } from '../types/social';

interface RawRequest {
  id: string;
  from_user_id: string;
  to_user_id: string;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
}

export type SendRequestReason =
  | 'already_friends'
  | 'request_exists'
  | 'yourself'
  | 'rate_limit_hourly'
  | 'too_many_pending'
  | 'rate_limited'
  | 'error';

export interface SendRequestResult {
  ok: boolean;
  reason?: SendRequestReason;
}

export type AcceptRequestReason =
  | 'not_authenticated'
  | 'not_found'
  | 'not_recipient'
  | 'not_pending'
  | 'error';

export interface AcceptRequestResult {
  ok: boolean;
  reason?: AcceptRequestReason;
}

export function describeSendRequestError(reason: SendRequestReason | undefined): string {
  switch (reason) {
    case 'already_friends':
      return 'Vocês já são amigos.';
    case 'request_exists':
      return 'Já existe um pedido pendente.';
    case 'yourself':
      return 'Você não pode adicionar a si mesmo.';
    case 'rate_limit_hourly':
      return 'Você atingiu o limite de pedidos por hora. Tente novamente mais tarde.';
    case 'too_many_pending':
      return 'Você tem muitos pedidos aguardando resposta. Aguarde algumas serem respondidas antes de enviar mais.';
    case 'rate_limited':
    default:
      return 'Não foi possível enviar o pedido agora. Tente novamente mais tarde.';
  }
}

export interface UseFriendRequestsResult {
  incoming: FriendRequest[];
  outgoing: FriendRequest[];
  incomingCount: number;
  loading: boolean;
  sendRequest: (toUserId: string) => Promise<SendRequestResult>;
  acceptRequest: (requestId: string) => Promise<AcceptRequestResult>;
  rejectRequest: (requestId: string) => Promise<void>;
  cancelRequest: (requestId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

interface Options {
  onAccepted?: () => void;
}

export function useFriendRequests(
  userId: string | undefined,
  options: Options = {}
): UseFriendRequestsResult {
  const [incoming, setIncoming] = useState<FriendRequest[]>([]);
  const [outgoing, setOutgoing] = useState<FriendRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const onAccepted = options.onAccepted;

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const [{ data: incRows }, { data: outRows }] = await Promise.all([
      supabase
        .from('friend_requests')
        .select('id, from_user_id, to_user_id, status, created_at')
        .eq('to_user_id', userId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false }),
      supabase
        .from('friend_requests')
        .select('id, from_user_id, to_user_id, status, created_at')
        .eq('from_user_id', userId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false }),
    ]);

    const otherIds = Array.from(
      new Set<string>([
        ...((incRows ?? []) as RawRequest[]).map((r) => r.from_user_id),
        ...((outRows ?? []) as RawRequest[]).map((r) => r.to_user_id),
      ])
    );

    const profilesById: Record<string, { display_name?: string; username?: string }> = {};
    if (otherIds.length > 0) {
      const { data: profs } = await supabase
        .from('profiles_public')
        .select('user_id, display_name, username')
        .in('user_id', otherIds);
      for (const p of (profs ?? []) as Array<{
        user_id: string;
        display_name?: string;
        username?: string;
      }>) {
        profilesById[p.user_id] = { display_name: p.display_name, username: p.username };
      }
    }

    const enrich = (rows: RawRequest[], side: 'incoming' | 'outgoing'): FriendRequest[] =>
      rows.map((r) => {
        const otherId = side === 'incoming' ? r.from_user_id : r.to_user_id;
        const prof = profilesById[otherId] ?? {};
        return {
          ...r,
          other_display_name: prof.display_name ?? '',
          other_username: prof.username ?? '',
        };
      });

    setIncoming(enrich((incRows ?? []) as RawRequest[], 'incoming'));
    setOutgoing(enrich((outRows ?? []) as RawRequest[], 'outgoing'));
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sendRequest = useCallback(
    async (toUserId: string): Promise<SendRequestResult> => {
      if (!userId) return { ok: false, reason: 'error' };
      if (toUserId === userId) return { ok: false, reason: 'yourself' };

      // Cooldown de 7 dias apos rejeicao. Verificacao no cliente para
      // poupar uma roundtrip; o server (RPC + trigger) tem a validacao
      // autoritativa.
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: recentRejected } = await supabase
        .from('friend_requests')
        .select('id')
        .eq('from_user_id', userId)
        .eq('to_user_id', toUserId)
        .eq('status', 'rejected')
        .gte('created_at', sevenDaysAgo)
        .limit(1);
      if (recentRejected && recentRejected.length > 0) {
        return { ok: false, reason: 'rate_limited' };
      }

      const { error } = await supabase.rpc('send_friend_request', { p_to: toUserId });

      if (error) {
        const msg = (error.message || '').toLowerCase();
        // RPC usa 'cooldown' como nome interno; o client expoe como
        // 'rate_limited' (mensagem generica que nao revela rejeicao).
        if (msg.includes('cooldown')) return { ok: false, reason: 'rate_limited' };
        const reasons: SendRequestReason[] = [
          'rate_limit_hourly',
          'too_many_pending',
          'already_friends',
          'request_exists',
          'yourself',
        ];
        const matched = reasons.find((r) => msg.includes(r));
        return { ok: false, reason: matched ?? 'error' };
      }

      await load();
      return { ok: true };
    },
    [userId, load]
  );

  const acceptRequest = useCallback(
    async (requestId: string): Promise<AcceptRequestResult> => {
      if (!userId) return { ok: false, reason: 'not_authenticated' };

      // Chama RPC atômica: insere ambos os lados de friendships e deleta o
      // pedido em uma única transação (SECURITY DEFINER no servidor).
      // A UI só é atualizada após o sucesso para não mentir em caso de erro.
      const { error } = await supabase.rpc('accept_friend_request', {
        p_request_id: requestId,
      });

      if (error) {
        const msg = (error.message || '').toLowerCase();
        const reasons: AcceptRequestReason[] = [
          'not_authenticated',
          'not_found',
          'not_recipient',
          'not_pending',
        ];
        const matched = reasons.find((r) => msg.includes(r));
        return { ok: false, reason: matched ?? 'error' };
      }

      setIncoming((prev) => prev.filter((r) => r.id !== requestId));
      onAccepted?.();
      return { ok: true };
    },
    [userId, onAccepted]
  );

  const rejectRequest = useCallback(
    async (requestId: string) => {
      setIncoming((prev) => prev.filter((r) => r.id !== requestId));
      await supabase
        .from('friend_requests')
        .update({ status: 'rejected' })
        .eq('id', requestId);
    },
    []
  );

  const cancelRequest = useCallback(
    async (requestId: string) => {
      setOutgoing((prev) => prev.filter((r) => r.id !== requestId));
      await supabase.from('friend_requests').delete().eq('id', requestId);
    },
    []
  );

  return {
    incoming,
    outgoing,
    incomingCount: incoming.length,
    loading,
    sendRequest,
    acceptRequest,
    rejectRequest,
    cancelRequest,
    refresh: load,
  };
}
